import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  User,
  Leaf,
  Flame,
  Activity,
  Home,
  TrendingUp,
  Zap,
  AlertCircle,
  ArrowRight,
  Trophy // Imported Trophy icon
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProgressSkeleton from '../components/skeletons/ProgressSkeleton';
import ProBadge from '../components/ProBadge';
import NutritionStreakCard from '../components/NutritionStreakCard';
import Subscription from './Subscription';
import { useDataCache } from '../contexts/DataCacheContext';

type ActivePage = 'home' | 'progress' | 'activity' | 'profile';

interface ProgressProps {
  onBack: () => void;
  onNavigate: (page: ActivePage) => void;
  onOpenSubscription?: () => void;
  onStartOnboarding?: () => void;
}

interface UserProfile {
  name: string;
  chronological_age: number | null;
  biological_age: number | null;
  gender: string;
  country: string;
  onboarding_completed: boolean;
}

export default function Progress({ onBack, onNavigate, onOpenSubscription, onStartOnboarding }: ProgressProps) {
  const {
    userProfile,
    userId,
    streakData,
    ageTrajectoryData,
    loadUserProfile,
    loadStreakData,
    loadAgeTrajectoryData,
    isLoadingProfile
  } = useDataCache();

  const [showSubscription, setShowSubscription] = useState(false);

  useEffect(() => {
    loadUserProfile();
    loadStreakData();
    loadAgeTrajectoryData();
  }, [loadUserProfile, loadStreakData, loadAgeTrajectoryData]);

  const currentStreak = streakData?.currentStreak || 0;
  const bestStreak = streakData?.bestStreak || 0;

  if (isLoadingProfile || !userProfile) {
    return <ProgressSkeleton />;
  }

  if (showSubscription) {
    return <Subscription onBack={() => setShowSubscription(false)} />;
  }

  const onboardingIncomplete = !userProfile.onboarding_completed;
  const ageDifference = userProfile.biological_age && userProfile.chronological_age
    ? userProfile.chronological_age - userProfile.biological_age
    : 0;
  const isYounger = ageDifference > 0;

  const chartData = ageTrajectoryData.map((item, index) => {
    const date = new Date(item.date);
    const dayOfMonth = date.getDate();
    return {
      date: dayOfMonth.toString(),
      age: item.biological_age,
    };
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-2xl mx-auto">
        <header className="bg-white px-6 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Welcome, {userProfile.name.split(' ')[0]}</h1>
              <p className="text-xs text-gray-500">Let's get healthier today</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ProBadge onClick={() => setShowSubscription(true)} />
          </div>
        </header>

        <div className="p-4 space-y-4">
          {onboardingIncomplete && (
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-3.5 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="text-white" size={20} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-sm mb-1">Complete Your Health Profile</h3>
                  <p className="text-white/90 text-xs leading-snug">
                    Discover your biological age and get personalized health insights by completing the onboarding assessment.
                  </p>
                </div>
                <button
                  onClick={() => onStartOnboarding && onStartOnboarding()}
                  className="bg-white text-orange-600 px-3 py-2 rounded-lg font-bold text-xs hover:bg-orange-50 transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
                >
                  Complete
                  <ArrowRight size={14} strokeWidth={3} />
                </button>
              </div>
            </div>
          )}

          {!onboardingIncomplete && (
            <div className="grid grid-cols-2 gap-4">
              {/* Biological Age Card */}
              <div className="bg-white rounded-2xl p-5 border border-gray-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Leaf className="text-green-500" size={20} />
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      isYounger
                        ? 'text-green-600 bg-green-50'
                        : 'text-orange-600 bg-orange-50'
                    }`}>
                      {isYounger ? '-' : '+'}{Math.abs(ageDifference).toFixed(1)}y
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Bio-Age</p>
                </div>
                <p className="text-3xl font-black text-gray-900">{userProfile.biological_age}</p>
              </div>

              {/* NEW SPLIT STREAK CARD */}
              <div className="bg-white rounded-2xl p-4 border border-gray-200 flex flex-col justify-between">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="text-orange-500 fill-orange-500" size={18} />
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Consistency</span>
                </div>
                
                <div className="flex items-end justify-between">
                  {/* Current Streak */}
                  <div>
                    <p className="text-3xl font-black text-gray-900 leading-none mb-1">{currentStreak}</p>
                    <p className="text-[10px] font-semibold text-gray-400">Current</p>
                  </div>
                  
                  {/* Vertical Divider */}
                  <div className="w-px h-8 bg-gray-100 mx-1"></div>
                  
                  {/* Best Streak */}
                  <div className="text-right">
                     <div className="flex items-center justify-end gap-1">
                        <Trophy className="text-yellow-500" size={14} strokeWidth={2.5} />
                        <p className="text-xl font-bold text-gray-700 leading-none">{bestStreak}</p>
                     </div>
                    <p className="text-[10px] font-semibold text-gray-400 mt-1">Best Streak</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Incomplete Fallback */}
          {onboardingIncomplete && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-gray-200 opacity-50">
                <div className="flex items-center justify-between mb-3">
                  <Leaf className="text-gray-400" size={20} />
                  <span className="text-xs font-bold px-2 py-1 rounded-full text-gray-400 bg-gray-100">
                    --
                  </span>
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Bio-Age</p>
                <p className="text-3xl font-black text-gray-400">--</p>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <Flame className="text-orange-500" size={20} />
                  <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">New</span>
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Streak</p>
                <p className="text-3xl font-black text-gray-900">0</p>
              </div>
            </div>
          )}

          {!onboardingIncomplete && userId && (
            <NutritionStreakCard userId={userId} />
          )}

          {!onboardingIncomplete && userProfile.biological_age && (
            <div className="bg-white rounded-2xl p-5 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Age Trajectory</p>
                  <p className="text-sm text-gray-700 font-medium">Past 30 days</p>
                </div>
                <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-full flex items-center gap-1">
                  <TrendingUp size={12} strokeWidth={3} />
                  Down
                </span>
              </div>

              <div className="h-48 -mx-2 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#9ca3af"
                      style={{ fontSize: '11px', fontWeight: '600' }}
                      tickMargin={8}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      style={{ fontSize: '11px', fontWeight: '600' }}
                      domain={[
                        (dataMin: number) => Math.floor(dataMin - 0.5),
                        (dataMax: number) => Math.ceil(dataMax + 0.5)
                      ]}
                      tickMargin={8}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        padding: '8px 12px',
                      }}
                      labelStyle={{ color: '#374151', fontWeight: '700', fontSize: '12px' }}
                      formatter={(value: number) => [`${value}y`, 'Bio-Age']}
                      itemStyle={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="age"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: '#10b981', r: 4, strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                isYounger
                  ? 'bg-green-50 border-green-100'
                  : 'bg-blue-50 border-blue-100'
              }`}>
                <Zap className={`flex-shrink-0 ${isYounger ? 'text-green-600' : 'text-blue-600'}`} size={20} strokeWidth={2.5} />
                <p className={`text-xs font-semibold leading-relaxed ${
                  isYounger ? 'text-green-800' : 'text-blue-800'
                }`}>
                  {isYounger
                    ? `Amazing! You're ${Math.abs(ageDifference).toFixed(1)} years younger biologically!`
                    : ageDifference < 0
                    ? `Your biological age is ${Math.abs(ageDifference).toFixed(1)} years ahead. Let's work on reversing it!`
                    : `Your biological age matches your chronological age. Let's make you younger!`}
                </p>
              </div>
            </div>
          )}

          {/* Motivational Footer */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200 text-center">
            <p className="text-gray-600 text-sm font-bold uppercase tracking-wider mb-1">
              Living Younger
            </p>
            <p className="text-cyan-600 text-2xl font-black mb-2">
              ONE DAY AT A TIME
            </p>
            <p className="text-gray-500 text-xs font-medium">
              Keep tracking your wellness journey 💪
            </p>
          </div>
        </div>

        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 safe-area-inset-bottom">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
            <button
              onClick={() => onNavigate('home')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <Home size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Home</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-cyan-600 min-w-[60px] relative">
              <div className="absolute -top-1 w-12 h-0.5 bg-cyan-600 rounded-full"></div>
              <TrendingUp size={22} strokeWidth={2.5} />
              <span className="text-[10px] font-bold">Progress</span>
            </button>
            <button
              onClick={() => onNavigate('activity')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <Activity size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Activity</span>
            </button>
            <button
              onClick={() => onNavigate('profile')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <User size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Profile</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}
